import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import ApiService from '../../functions/apiService';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import moment from 'moment/moment';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useReactToPrint } from "react-to-print";
import ButtonTheme from '../../components/ThemeButton/ThemeButton';
import AuthContext from '../../context/AuthContext';
import SweetAlert from 'react-bootstrap-sweetalert';
import CustomSelect from '../../components/Schedule/CustomSelect';
import { setPossitionListDDL } from '../../store/ddlReducer';
import { toast } from 'react-toastify';
import { varFormatTime } from '../../utils/helperFunctions';

export default function RunCard() {
  const dispatch = useDispatch();

  const [data, setData] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [nightData, setNightData] = useState(null);
  const [detailAllData, setDetailAllData] = useState([]);
  const [loading, setLoading] = useState(true);

  const { pagesName, user, v3Data } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const routeDate = searchParams.get('date') || new Date();

  const printRef = useRef(null);

  const [showAlert, setShowAlert] = useState(false);
  const [positionData, setPositionData] = useState({});

  const detailData = useSelector((state) => state.dashboardReducer.Data);
  const PossitionListDDL = useSelector((state) => state.ddlReducer.PossitionListDDL) || [];

  const groupName = pagesName?.GROUP_HEADER_TEXT || "Group";

  // ---------- SAFE HELPERS ----------
  const safeStr = (v) => (v === null || v === undefined ? '' : String(v));
  const safeTrim = (v) => safeStr(v).trim();

  const parseNameFromParen = (txt) => {
    const s = safeStr(txt);
    if (!s) return '';
    const m = s.match(/\(([^)]+)\)/);
    return safeTrim(m?.[1] || '');
  };

  // ---------- SCOPED STYLES (SCREEN + PRINT LAYOUT) ----------
  useEffect(() => {
    const styleId = 'runcard-employee-view-styles-v4';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.type = 'text/css';
    style.appendChild(document.createTextNode(`
      .rc-scope .rc-top{ padding:16px; border-bottom:1px solid #EAECF0; }
      .rc-scope .rc-topRow{ display:flex; align-items:flex-start; gap:12px; margin-top:10px; }
      .rc-scope .rc-actions{ margin-left:auto; display:flex; align-items:center; gap:12px; }
      .rc-scope .rc-title{ margin:0; font-weight:900; font-size:18px; line-height:1.25; }
      .rc-scope .noPrint{ }

      #runcardPrintRoot{ padding:16px; }
      #runcardPrintRoot .rc-printHeader{
        display:flex; justify-content:space-between; align-items:baseline;
        border-bottom:1px solid #EAECF0; padding-bottom:8px; margin-bottom:12px;
      }
      #runcardPrintRoot .rc-printHeader .lh{ font-weight:900; }
      #runcardPrintRoot .rc-printHeader .rh{ font-weight:800; }

      #runcardPrintRoot .section{
        border:1px solid #E5E7EB;
        border-radius:12px;
        overflow:hidden;
        background:#fff;
        margin-bottom:14px;
      }
      #runcardPrintRoot .sectionHead{
        padding:10px 12px;
        color:#fff;
        font-weight:900;
        font-size:12px;
        letter-spacing:.2px;
      }
      #runcardPrintRoot .sectionBody{ padding:12px; }

      #runcardPrintRoot .empCard{
        border:1px solid #EEF2F7;
        border-radius:10px;
        padding:10px;
        margin-bottom:10px;
        page-break-inside:auto;
        break-inside:auto;
      }
      #runcardPrintRoot .empHeader{
        display:flex; justify-content:space-between; gap:10px; margin-bottom:8px;
      }
      #runcardPrintRoot .empName{ font-weight:900; font-size:13px; line-height:1.2; }
      #runcardPrintRoot .empMeta{ color:#667085; font-weight:800; font-size:12px; white-space:nowrap; }

      #runcardPrintRoot .line{
        display:grid;
        grid-template-columns:220px 1fr;
        gap:10px;
        padding:8px 0;
        border-top:1px solid #EEF2F7;
      }
      #runcardPrintRoot .line:first-child{ border-top:none; padding-top:0; }
      #runcardPrintRoot .unitCell{ font-weight:900; font-size:12px; line-height:1.2; word-break:break-word; }
      #runcardPrintRoot .nameCell{ font-size:12px; line-height:1.2; word-break:break-word; }

      #runcardPrintRoot .shift-arr{ margin-top:2px; line-height:1.2; }

      #runcardPrintRoot .detailRow{
        border-top:1px solid #EEF2F7;
        padding:8px 0;
        font-size:12px;
        line-height:1.25;
      }
      #runcardPrintRoot .detailRow:first-child{ border-top:none; padding-top:0; }
      #runcardPrintRoot .detailTitle{ font-weight:900; margin-bottom:4px; }
      #runcardPrintRoot .k{ color:#667085; font-weight:900; margin-right:6px; }

      @media (max-width: 768px){
        #runcardPrintRoot .line{ grid-template-columns:1fr; }
      }
    `));
    document.head.appendChild(style);
  }, []);

  // ---------- DATA ----------
  const loadDropdown = () => {
    ApiService.get('/getPositionListDDL', {}, 'getPositionListDDL')
      .then(({ data }) => dispatch(setPossitionListDDL(data)))
      .catch((err) => console.log('getPositionListDDL err', err));
  };

  const loadData = (loadingParam = true) => {
    if (loadingParam) setLoading(true);

    ApiService.get('/todayShift?date=' + moment(routeDate).format('DD-MM-YYYY'), {}, 'todayShift')
      .then(({ data }) => {
        setData(data);

        if (data) {
          const tempNight = JSON.parse(JSON.stringify(data));
          tempNight.stations = (tempNight.stations || []).map(station => ({
            ...station,
            employees: (station.employees || []).filter(emp => emp.inNIGHTShift)
          }));
          setNightData(tempNight);

          const tempDay = JSON.parse(JSON.stringify(data));
          tempDay.stations = (tempDay.stations || []).map(station => ({
            ...station,
            employees: (station.employees || []).filter(emp => emp.inDayShift)
          }));
          setDayData(tempDay);
        }

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        toast.error("Error");
      });
  };

  useEffect(() => {
    loadData(true);
    loadDropdown();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    ApiService.get('/todayDetails', { date: detailData?.date }, 'todayDetails')
      .then(({ data }) => setDetailAllData(data?.data || []))
      .catch(() => setDetailAllData([]));
    // eslint-disable-next-line
  }, [data]);

  // ---------- OPTIONS (label fallback prevents hidden .trim() inside select libs) ----------
  const options = useMemo(() => {
    return (PossitionListDDL || []).map((i) => ({
      value: i?.SO_Value,
      label: safeStr(i?.SO_Desc || i?.SO_Value || ''), // never undefined
      dataAssignment: i?.resource_assignment,
    }));
    // eslint-disable-next-line
  }, [PossitionListDDL]);

  const handleSelectChange = (e, row, dayOrNight) => {
    setPositionData({ e, emp: row, day: dayOrNight });
    setShowAlert(true);
  };

  const changeEmpPossion = () => {
    try {
      if (positionData?.emp?.Position_Code == positionData?.e?.value) {
        toast.error("Same Position");
        setShowAlert(false);
        return;
      }
      if (!positionData?.e?.dataAssignment) {
        toast.error("No assignment for this position");
        setShowAlert(false);
        return;
      }

      ApiService.post('/changeEmployeeSchedule', {
        EmpCode: positionData.emp.Emp_Code,
        Possition: positionData.e.value,
        Assignment: positionData.e.dataAssignment.Assignment,
        Vehicle: positionData.e.dataAssignment.Vehicle || null,
        ShiftDate: data?.date,
        ShiftCode: positionData.day === 'day' ? 2 : 3
      })
        .then(() => loadData(true))
        .catch((err) => console.log('changeEmployeeSchedule err', err));
    } finally {
      setShowAlert(false);
    }
  };

  // ---------- NAME CELL ----------
  const getEmpNameStyle = (row, type = 'day') => {
    if (!row) return null;

    let color = row.EA_Status === 'N' ? 'orange' : 'inherit';
    let textEmployee = safeTrim(`${safeStr(row.EA_Rank)} ${safeStr(row.fullName)}`);
    let reason = '';

    if (type === 'night') {
      const arr = row?.customShiftsArrayNight || [];
      if (arr.length > 0) {
        return (
          <>
            <div className="shift-arr">{textEmployee}</div>
            {arr.map((shift, idx) => {
              reason = safeStr(shift.absentReason);
              let line = `, Open Position (${varFormatTime(shift.custom_time, user?.Time_Format)})`;

              if (shift.dayStatus === 'F') {
                reason = '';
                line = `${safeStr(shift.DayFillEmpFirst_name)} ${safeStr(shift.DayFIllEmpLast_name)} (${varFormatTime(shift.custom_time, user?.Time_Format)})`;
              }

              let c = 'inherit';
              if (shift.absentColor === 'red') c = '#E62719';
              else if (shift.absentColor === 'orange') c = 'orange';
              else if (shift.absentColor === 'green') c = '#079E46';
              else if (shift.absentColor === 'blue') c = '#8cbaf9';

              return (
                <div
                  key={idx}
                  className="shift-arr"
                  style={{
                    color: c,
                    fontWeight: row.EA_Status === 'N' ? 'bold' : 'normal',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  <span style={{ fontWeight: 'bold' }}>{reason}</span>
                  {line}
                </div>
              );
            })}
          </>
        );
      }

      if (row.nightStatus === "F" && row.NightFillEmpFirst_name) {
        textEmployee = safeTrim(`${safeStr(row.EA_Rank)} ${safeStr(row.NightFillEmpFirst_name)} ${safeStr(row.NightFIllEmpLast_name)}`);
        color = '#079E46';
      }
      if (row.nightStatus === "O") { reason = safeStr(row.absentReasonNight); textEmployee = `Open Position`; color = '#E62719'; }
      if (row.nightStatus === "D") { reason = safeStr(row.absentReasonNight); textEmployee = `Open Position`; color = '#000000'; }
      if (row.nightStatus === "Q") { reason = safeStr(row.absentReasonNight); textEmployee = `Open Position`; color = '#FFA500'; }

      return (
        <div className="shift-arr" style={{ color, fontWeight: row.EA_Status === 'N' ? 'bold' : 'normal' }}>
          <span style={{ fontWeight: 'bold' }}>{reason}</span>
          {reason ? ' ' : ''}{textEmployee}
        </div>
      );
    }

    const arr = row?.customShiftsArrayDay || [];
    if (arr.length > 0) {
      return (
        <>
          <div className="shift-arr">{textEmployee}</div>
          {arr.map((shift, idx) => {
            reason = safeStr(shift.absentReason);
            let line = `, Open Position (${varFormatTime(shift.custom_time, user?.Time_Format)})`;

            if (shift.dayStatus === 'F') {
              reason = '';
              line = `${safeStr(shift.DayFillEmpFirst_name)} ${safeStr(shift.DayFIllEmpLast_name)} (${varFormatTime(shift.custom_time, user?.Time_Format)})`;
            }

            let c = 'inherit';
            if (shift.absentColor === 'red') c = '#E62719';
            else if (shift.absentColor === 'orange') c = 'orange';
            else if (shift.absentColor === 'green') c = '#079E46';
            else if (shift.absentColor === 'blue') c = '#8cbaf9';

            return (
              <div
                key={idx}
                className="shift-arr"
                style={{
                  color: c,
                  fontWeight: row.EA_Status === 'N' ? 'bold' : 'normal',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                <span style={{ fontWeight: 'bold' }}>{reason}</span>
                {line}
              </div>
            );
          })}
        </>
      );
    }

    if (row.dayStatus === "F" && row.DayFillEmpFirst_name) {
      textEmployee = safeTrim(`${safeStr(row.EA_Rank)} ${safeStr(row.DayFillEmpFirst_name)} ${safeStr(row.DayFIllEmpLast_name)}`);
      color = '#079E46';
    }
    if (row.dayStatus === "O") { reason = safeStr(row.absentReason); textEmployee = `Open Position`; color = '#E62719'; }
    if (row.dayStatus === "D") { reason = safeStr(row.absentReason); textEmployee = `Open Position`; color = '#000000'; }
    if (row.dayStatus === "Q") { reason = safeStr(row.absentReason); textEmployee = `Open Position`; color = '#FFA500'; }

    return (
      <div className="shift-arr" style={{ color, fontWeight: row.EA_Status === 'N' ? 'bold' : 'normal' }}>
        <span style={{ fontWeight: 'bold' }}>{reason}</span>
        {reason ? ' ' : ''}{textEmployee}
      </div>
    );
  };

  // ---------- GROUPING ----------
  const shiftsByEmployee = useMemo(() => {
    const map = new Map();

    const keyFor = (emp, stationName, shiftType) => {
      if (emp?.Emp_Code !== null && emp?.Emp_Code !== undefined) return `EMP-${String(emp.Emp_Code)}`;
      const pos = safeTrim(emp?.Position_Code || emp?.PositionName || 'POS');
      return `OPEN-${shiftType}-${safeTrim(stationName)}-${pos}`;
    };

    const upsert = (emp, stationName, shiftType) => {
      const key = keyFor(emp, stationName, shiftType);
      if (!map.has(key)) {
        map.set(key, {
          key,
          Emp_Code: emp?.Emp_Code ?? null,
          fullName: emp?.fullName || 'Open Position',
          EA_Rank: emp?.EA_Rank || '',
          day: [],
          night: [],
        });
      }
      const rec = map.get(key);
      rec.fullName = emp?.fullName || rec.fullName;
      rec.EA_Rank = emp?.EA_Rank || rec.EA_Rank;
      rec[shiftType].push({ stationName, emp });
    };

    (dayData?.stations || []).forEach(st => (st?.employees || []).forEach(emp => upsert(emp, st?.name, 'day')));
    (nightData?.stations || []).forEach(st => (st?.employees || []).forEach(emp => upsert(emp, st?.name, 'night')));

    return Array.from(map.values()).sort((a, b) => safeStr(a.fullName).localeCompare(safeStr(b.fullName)));
  }, [dayData, nightData]); // eslint-disable-line

  const detailsByEmployee = useMemo(() => {
    const map = new Map();

    (detailAllData || []).forEach((d) => {
      const empCode = (d?.EmpCode !== null && d?.EmpCode !== undefined) ? String(d.EmpCode) : '';
      const key = empCode ? `EMP-${empCode}` : `NOEMP-${safeStr(d?.id)}`;

      if (!map.has(key)) {
        const n1 = parseNameFromParen(d?.StatusText);
        const n2 = parseNameFromParen(d?.RequestedText);
        const display = n1 || n2 || (d?.Rank ? `${safeStr(d.Rank)} (${v3Data?.V3_ALLOW_ALTER_LABEL_TEXTS === 'true' ? 'CPP' : 'Detail'})` : (v3Data?.V3_ALLOW_ALTER_LABEL_TEXTS === 'true' ? 'CPP' : 'Detail'));
        map.set(key, { key, EmpCode: empCode || null, displayName: display, items: [] });
      }

      map.get(key).items.push(d);
    });

    return Array.from(map.values()).sort((a, b) => safeStr(a.displayName).localeCompare(safeStr(b.displayName)));
  }, [detailAllData]); // eslint-disable-line

  const renderShiftLines = (rows, type) => {
    if (!rows || rows.length === 0) return null;

    return rows.map((r, idx) => {
      const emp = r.emp;
      const stationName = r.stationName || '';
      const defaultOption = options.find((opt) => opt?.value === emp?.Position_Code);

      return (
        <div className="line" key={`${type}-${idx}`}>
          <div>
            <div className="unitCell">
              {safeStr(stationName)} — {safeStr(emp?.PositionName || 'Unit')}
            </div>

            <div className="noPrint" style={{ marginTop: 6, maxWidth: 360 }}>
              <CustomSelect
                options={options}
                defaultOption={defaultOption}
                isDisabled={false}
                isLoading={false}
                isRtl={false}
                isSearchable={true}
                name="Possition"
                onChange={(e) => handleSelectChange(e, emp, type)}
                bgColor={'#e9e9ed'}
                value={defaultOption}
              />
            </div>
          </div>

          <div className="nameCell">
            {getEmpNameStyle(emp, type)}
          </div>
        </div>
      );
    });
  };

  const renderDetailItems = (items) => {
    return (items || []).map((d, idx) => (
      <div className="detailRow" key={idx}>
        <div className="detailTitle">
          {safeStr(d?.DetailType || (v3Data?.V3_ALLOW_ALTER_LABEL_TEXTS === 'true' ? 'CPP' : 'Detail'))} — {safeStr(d?.ShiftName || '')}
        </div>
        <div><span className="k">Status:</span>{safeStr(d?.StatusText || d?.StatusName || '-')}</div>
        <div><span className="k">Assignment:</span>{safeStr(d?.AssignmentText || '-')}</div>
        <div><span className="k">Requested:</span>{safeStr(d?.RequestedName || '-')}</div>
        <div><span className="k">Text:</span>{safeStr(d?.RequestedText || '-')}</div>
        <div><span className="k">Description:</span>{safeStr(d?.DetailText || d?.Details || '-')}</div>
      </div>
    ));
  };

  // ---------- PRINT (MOST RELIABLE) ----------
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Runcard-${moment(routeDate).format('YYYY-MM-DD')}`,

    // ✅ pageStyle is injected into the print iframe every time
    pageStyle: `
  @page { margin: 10mm; }
  html, body { margin: 0 !important; padding: 0 !important; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  /* ✅ Force exact colors everywhere */
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

  /* ✅ Isolation that DOES NOT break pagination */
  body * { visibility: hidden !important; }
  #runcardPrintRoot, #runcardPrintRoot * { visibility: visible !important; }

  /* keep in normal flow so it can go to page 2/3 */
  #runcardPrintRoot { position: static !important; width: 100% !important; }

  /* never print controls */
  .noPrint { display: none !important; }

  /* ✅ Fix: blue header text turning black in print */
  #runcardPrintRoot .sectionHead { color: #fff !important; }
  #runcardPrintRoot .sectionHead * { color: #fff !important; }
`,

  });

  const printFunction = () => {
    if (loading) {
      toast.info("Please wait, still loading data.");
      return;
    }
    if (!printRef.current) {
      toast.error("Nothing to print (print area not ready).");
      return;
    }
    handlePrint();
  };

  return (
    <>
      <div className="rc-scope">
        <Paper className='my-3 pb-4'>
          <div className="rc-top noPrint">
            <Box>
              <Link to='/firehouse/my-team' style={{ color: '#555555', textDecoration: 'none' }}>
                <ArrowBackIosIcon />
                <span className='fw-500'>Back</span>
              </Link>
            </Box>

            <div className="rc-topRow">
              <Typography variant="h6" className="rc-title">
                {moment(routeDate).format("dddd Do MMMM YYYY")} - {groupName}
              </Typography>

              <div className="rc-actions">
                <ButtonTheme onClick={printFunction} color='primary' disabled={loading}>
                  Print
                </ButtonTheme>
              </div>
            </div>
          </div>

          {loading ? (
            <Box sx={{ minHeight: '200px' }} className="w-100 h-100 d-flex justify-content-center align-items-center">
              <CircularProgress />
            </Box>
          ) : (
            <div ref={printRef} id="runcardPrintRoot">
              <div className="rc-printHeader">
                <div className="lh">Runcard (Employee View)</div>
                <div className="rh">{moment(routeDate).format("dddd MMMM DD, YYYY")}</div>
              </div>

              <div className="section">
                <div className="sectionHead" style={{ backgroundColor: '#23314a' }}>
                  Shift Assignments (Day + Night)
                </div>
                <div className="sectionBody">
                  {shiftsByEmployee.length === 0 ? (
                    <div style={{ color: '#667085', fontWeight: 700 }}>No shift assignments found.</div>
                  ) : (
                    shiftsByEmployee.map((rec) => (
                      <div className="empCard" key={rec.key}>
                        <div className="empHeader">
                          <div className="empName">{safeTrim(`${safeStr(rec.EA_Rank)} ${safeStr(rec.fullName)}`)}</div>
                          <div className="empMeta">{rec.Emp_Code ? `Emp: ${rec.Emp_Code}` : 'Open'}</div>
                        </div>

                        {rec.day.length > 0 && (
                          <div className="section" style={{ borderRadius: 10, marginBottom: 10 }}>
                            <div className="sectionHead" style={{ backgroundColor: '#cb243d' }}>Day</div>
                            <div className="sectionBody" style={{ padding: 10 }}>
                              {renderShiftLines(rec.day, 'day')}
                            </div>
                          </div>
                        )}

                        {rec.night.length > 0 && (
                          <div className="section" style={{ borderRadius: 10, marginBottom: 0 }}>
                            <div className="sectionHead" style={{ backgroundColor: '#23314a' }}>Night</div>
                            <div className="sectionBody" style={{ padding: 10 }}>
                              {renderShiftLines(rec.night, 'night')}
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="section">
                <div className="sectionHead" style={{ backgroundColor: '#555555' }}>
                  Details (Grouped by Employee)
                </div>
                <div className="sectionBody">
                  {detailsByEmployee.length === 0 ? (
                    <div style={{ color: '#667085', fontWeight: 700 }}>No details found.</div>
                  ) : (
                    detailsByEmployee.map((grp) => (
                      <div className="empCard" key={grp.key}>
                        <div className="empHeader">
                          <div className="empName">{safeStr(grp.displayName)}</div>
                          <div className="empMeta">{grp.EmpCode ? `Emp: ${grp.EmpCode}` : 'No EmpCode'}</div>
                        </div>
                        {renderDetailItems(grp.items)}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {showAlert && (
            <SweetAlert
              warning
              showCancel
              confirmBtnText="Confirm"
              confirmBtnBsStyle="danger"
              title="Employee Re-assignment"
              onConfirm={changeEmpPossion}
              onCancel={() => {
                loadData(false);
                setShowAlert(false);
              }}
              focusCancelBtn
            >
              Are you sure you want to re-assign this position?
            </SweetAlert>
          )}
        </Paper>
      </div>
    </>
  )
}
